/**
 * S3 Chat Integration Hooks
 *
 * These hooks integrate the S3 chat API with the new S3-based chat store
 * They handle the bridge between React Query mutations and Zustand store
 */

import React from 'react';
import { createLogger, randomUUID } from '@ctrl-enter/tools/utils';

const logger = createLogger('client:hook:use-s3-chat-integration');
import { useGetUserChats } from '../api/gateway-provider';
import {
  useS3ChatStore,
  type LocalChatMessage,
  type LocalChatSession,
} from '../stores/s3-chat-store';

/**
 * Main hook for S3 chat integration
 * Loads chat sessions from S3 backend and manages state
 */
export const useS3ChatIntegration = () => {
  const store = useS3ChatStore();

  // Load user chats from S3 backend
  const {
    data: chatsData,
    isLoading: isLoadingChats,
    error: chatsError,
    refetch,
  } = useGetUserChats();

  // Extract stable references to avoid infinite re-renders
  const setSessions = React.useCallback(store.setSessions, []);
  const setError = React.useCallback(store.setError, []);

  // Convert backend chat data to store format when data loads
  React.useEffect(() => {
    logger.info('🔍 [useS3ChatIntegration] Received chatsData:', {
      hasData: !!chatsData,
      isObject: typeof chatsData === 'object',
      hasBody: chatsData && 'body' in chatsData,
      keys: chatsData ? Object.keys(chatsData) : [],
    });

    if (chatsData && typeof chatsData === 'object' && 'body' in chatsData) {
      const responseBody = (chatsData as any).body;
      if (responseBody && 'chats' in responseBody && Array.isArray(responseBody.chats)) {
        if (responseBody.chats.length > 0) {
          logger.info('🔄 [useS3ChatIntegration] Converting backend chats to store format:', {
            chatCount: responseBody.chats.length,
          });

          // Convert backend chat list to LocalChatSession format
          const localSessions: LocalChatSession[] = responseBody.chats.map((chat: any) => ({
            id: chat.session_id,
            title: chat.title,
            messages: [], // Messages will be loaded separately when session is opened
            createdAt: chat.created_at,
            updatedAt: chat.updated_at,
            messageCount: chat.message_count,
            isArchived: chat.is_archived ?? false, // Default to false for legacy data
          }));

          // Store sessions using the proper setter method
          setSessions(localSessions);
          logger.info('✅ [useS3ChatIntegration] Successfully loaded sessions from S3:', {
            sessionCount: localSessions.length,
          });
        } else {
          logger.info('📝 [useS3ChatIntegration] No existing chats found - starting fresh');
          setSessions([]);
        }
      }
    }
  }, [chatsData, setSessions]); // Only depend on chatsData and stable setSessions

  // Handle errors
  React.useEffect(() => {
    if (chatsError) {
      logger.error('❌ [useS3ChatIntegration] Error loading chats:', chatsError);
      setError('Failed to load chat history');
    } else {
      setError(null);
    }
  }, [chatsError, setError]); // Use stable setError reference

  // Mock session creation (will be replaced with real API later)
  const createSession = React.useCallback(async (title?: string): Promise<string | null> => {
    logger.info('📝 [useS3ChatIntegration] Mock createSession called:', title);
    return randomUUID();
  }, []);

  const deleteSession = React.useCallback(async (sessionId: string): Promise<void> => {
    logger.info('🗑️ [useS3ChatIntegration] Mock deleteSession called:', sessionId);
  }, []);

  const setCurrentSession = React.useCallback(
    (sessionId: string | null) => {
      logger.info('🎯 [useS3ChatIntegration] setCurrentSession called:', sessionId);
      store.setCurrentSession(sessionId);
    },
    [store]
  );

  return {
    // Store state
    sessions: store.sessions,
    currentSessionId: store.currentSessionId,
    isLoading: isLoadingChats,
    error: store.lastError,

    // Actions
    createSession,
    deleteSession,
    setCurrentSession,
    clearError: store.clearError,
    refetch,
  };
};

/**
 * Hook for managing a specific chat session and its messages
 * TEMPORARY: Simplified to prevent infinite loops
 */
export const useChatSession = (sessionId: string | null) => {
  const store = useS3ChatStore();

  // Get current session from store
  const session = store.getSession(sessionId || '');

  // Simple mock functions
  const addMessage = React.useCallback((message: Omit<LocalChatMessage, 'id'>) => {
    logger.info('📝 [useChatSession] Mock addMessage:', message);
    return randomUUID();
  }, []);

  const saveMessage = React.useCallback(async (message: LocalChatMessage): Promise<void> => {
    logger.info('💾 [useChatSession] Mock saveMessage:', message);
  }, []);

  return {
    session,
    isLoadingMessages: false,
    addMessage,
    saveMessage,
    updateMessageContent: () => {
      /* no-op */
    },
    updateMessageStatus: () => {
      /* no-op */
    },
    removeOptimisticMessage: () => {
      /* no-op */
    },
  };
};

/**
 * Hook for file uploads in chat sessions
 * TEMPORARY: Simplified to prevent infinite loops
 */
export const useS3ChatFileUpload = () => {
  const uploadFile = React.useCallback(
    async (
      sessionId: string,
      fileName: string,
      fileContent: string,
      mimeType: string
    ): Promise<string | null> => {
      logger.info('📎 [useS3ChatFileUpload] Mock upload:', { sessionId, fileName, mimeType });
      return randomUUID();
    },
    []
  );

  return {
    uploadFile,
    isUploading: false,
  };
};

/**
 * Hook for integrating with the AI askExpert endpoint
 * TEMPORARY: Simplified to prevent infinite loops
 */
export const useAskExpertWithHistory = () => {
  const askExpertWithHistory = React.useCallback(
    async (params: {
      sessionId?: string;
      userInput: string;
      chatHistory?: any[];
      screenshots?: any[];
      attachments?: any[];
    }) => {
      logger.info('🤖 [useAskExpertWithHistory] Mock AI call:', params);
      return {
        status: 200,
        body: new ReadableStream({
          start(controller) {
            // Convert string to ArrayBuffer as expected by TextDecoder
            const encoder = new TextEncoder();
            const chunk = encoder.encode('Mock AI response from S3 integration.');
            controller.enqueue(chunk);
            controller.close();
          },
        }),
      };
    },
    []
  );

  return {
    askExpertWithHistory,
    isLoading: false,
  };
};
